import { MappingContext } from '@app/mapping/MappingContext';
import { JSX, FC, useCallback, useContext, useRef, useState, useEffect } from 'react';
import * as ReactMapGL from "react-map-gl/mapbox";
import { Source, Layer, NavigationControl, type MapRef } from "react-map-gl/mapbox";
import LoadingSpinner from '@app/shared/LoadingSpinner';
import { RecenterControl } from '@app/shared/mapping/RecenterControl';
import { MapboxSupportAlert } from '@app/shared/mapping/MapboxSupportAlert';
import { MarketExpansionMapDataContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionMapDataContext";
import { ProviderLandscapeMapDataContext } from "@app/overviews/market-expansion-dashboard/contexts/ProviderLandscapeMapDataContext";
import { MarketExpansionFiltersContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionFiltersContext";
import {
  COUNTY_OUTLINE_COLORS,
  COUNTY_SHADING_BASE,
  EMPTY_FEATURE_COLLECTION,
  HOVERED_COUNTY_BORDER_LAYER_ID,
  HOVERED_COUNTY_SOURCE_ID,
  MAP_DEFAULT_ZOOM,
  SELECTED_COUNTY_BORDER_LAYER_ID,
  SELECTED_COUNTY_SOURCE_ID,
  PROVIDER_LANDSCAPE_SHADED_COUNTY_BORDER_LAYER_ID,
  PROVIDER_LANDSCAPE_SHADED_COUNTY_FILL_LAYER_ID,
  PROVIDER_LANDSCAPE_SHADED_COUNTY_SOURCE_ID,
} from '@app/overviews/market-expansion-dashboard/MarketExpansionDashboardConstants';
import { MapAppearanceControl } from "@app/shared/map-appearance/components/MapAppearanceControl";
import { ProviderLandscapeColorTheme } from "@app/overviews/market-expansion-dashboard/contexts/ProviderLandscapeColorThemeContext";
import GradientLegend from '@app/overviews/market-expansion-dashboard/components/GradientLegend';
import ProviderLandscapeCountySummary from '@app/overviews/market-expansion-dashboard/components/provider-landscape-map/ProviderLandscapeCountySummary';

const WrappedProviderLandscapeMap: FC = (): JSX.Element => {
  const mappingContext = useContext(MappingContext);
  const mapDataContext = useContext(MarketExpansionMapDataContext);
  const providerLandscapeMapDataContext = useContext(ProviderLandscapeMapDataContext);
  const filtersContext = useContext(MarketExpansionFiltersContext);
  const mapRef = useRef<MapRef>(null);

  const { getColor } = ProviderLandscapeColorTheme.useMapColorTheme();
  const minCaseCountColor = getColor("minCaseCountColor");
  const maxCaseCountColor = getColor("maxCaseCountColor");

  const imagesDir = `${window.customUIBaseUrl}/images/`;

  const [viewState, setViewState] = useState<ReactMapGL.ViewState>({ ...mappingContext.defaultViewState, zoom: MAP_DEFAULT_ZOOM });

  const onMapLoad = useCallback((): void => {
    const map = mapRef.current?.getMap();

    if (!map) return;

    mapRef.current?.setCenter([mappingContext.defaultLongitude, mappingContext.defaultLatitude]);

    mapRef.current?.on('mousemove', PROVIDER_LANDSCAPE_SHADED_COUNTY_FILL_LAYER_ID, (e) => mapDataContext.handleMouseMoveOnCounty(e));
    mapRef.current?.on('mouseleave', PROVIDER_LANDSCAPE_SHADED_COUNTY_FILL_LAYER_ID, () => mapDataContext.handleMouseLeaveOnCounty());
    mapRef.current?.on('click', PROVIDER_LANDSCAPE_SHADED_COUNTY_FILL_LAYER_ID, (e) => mapDataContext.handleMouseClickOnCounty(e));
  }, []);

  const onMapMove = useCallback((viewState: ReactMapGL.ViewState): void => {
    setViewState({
      ...viewState,
      // Disables the map rotation changes (keyboard, mouse, touch)
      bearing: mappingContext.defaultBearing,
      // Disables the map pitch changes (keyboard, mouse, touch)
      pitch: mappingContext.defaultPitch
    });
  }, []);

  const recenterMap = useCallback((): void => {
    setViewState({ ...mappingContext.defaultViewState, zoom: MAP_DEFAULT_ZOOM });
    mapDataContext.setMinZoomLevel(mappingContext.defaultZoom - .5);
  }, []);

  const handleRecenterIconClick = () => {
    if (mapDataContext.dataLoaded && !!mapDataContext.dataBoundingBox) {
      mapRef.current?.fitBounds(mapDataContext.dataBoundingBox, mapDataContext.easingOptions());
    } else {
      recenterMap();
    }
  }

  const maxCaseCount = providerLandscapeMapDataContext.providerLandscapeShadedCountyData ? Math.max(
    ...providerLandscapeMapDataContext.providerLandscapeShadedCountyData.features.map(
      (f) => f.properties?.totalCaseCount ?? 0
    )
  ) : 0;

  const sqrtMaxCaseCount = Math.sqrt(Math.max(maxCaseCount, 1));

  useEffect(() => {
    if (providerLandscapeMapDataContext.providerLandscapeShadedCountyData) {
      handleRecenterIconClick();
    }
  }, [mapDataContext.dataBoundingBox, mapDataContext.dataLoaded, mapDataContext.selectedTooltipHeight, providerLandscapeMapDataContext.providerLandscapeShadedCountyData]);

  useEffect(() => {
    if (!filtersContext.selectedState) {
      mapDataContext.resetMapData();
      recenterMap();
    }
  }, [filtersContext.selectedState])

  return (
    <MapboxSupportAlert>
      <div className="market-expansion-dashboard__map-container" data-testid="market-expansion-provider-landscape__map">
        <ReactMapGL.Map
          {...viewState}
          {...mappingContext.defaultMapProps}
          reuseMaps={true}
          onLoad={onMapLoad}
          onMove={(e) => onMapMove(e.viewState)}
          ref={mapRef}
        >
          {/* Layers are sorted from top-most to bottom-most and using beforeId to avoid mapbox console errors */}

          <Source id={HOVERED_COUNTY_SOURCE_ID} type="geojson" data={mapDataContext.hoverCountyBorderData ?? EMPTY_FEATURE_COLLECTION}>
            <Layer
              id={HOVERED_COUNTY_BORDER_LAYER_ID}
              type="line"
              paint={{
                "line-color": COUNTY_OUTLINE_COLORS.hovered,
                "line-offset": 0.5,
                "line-width": 4,
              }}
            />
          </Source>

          <Source id={SELECTED_COUNTY_SOURCE_ID} type="geojson" data={mapDataContext.selectedCountyBorderData ?? EMPTY_FEATURE_COLLECTION}>
            {providerLandscapeMapDataContext.providerLandscapeShadedCountyData && (
              <Layer
                id={SELECTED_COUNTY_BORDER_LAYER_ID}
                beforeId={HOVERED_COUNTY_BORDER_LAYER_ID}
                type="line"
                paint={{
                  "line-color": COUNTY_OUTLINE_COLORS.selected,
                  "line-width": 1.6,
                }}
              />
            )}
          </Source>

          <Source id={PROVIDER_LANDSCAPE_SHADED_COUNTY_SOURCE_ID} type="geojson" data={providerLandscapeMapDataContext.providerLandscapeShadedCountyData ?? EMPTY_FEATURE_COLLECTION}>
            {/* County outline layer. */}
            <Layer
              id={PROVIDER_LANDSCAPE_SHADED_COUNTY_BORDER_LAYER_ID}
              beforeId={SELECTED_COUNTY_BORDER_LAYER_ID}
              type="line"
              paint={{
                "line-color": "grey",
                "line-offset": 0,
                "line-width": 1,
              }}
            />

            {/* County fill layer. */}
            <Layer
              id={PROVIDER_LANDSCAPE_SHADED_COUNTY_FILL_LAYER_ID}
              beforeId={PROVIDER_LANDSCAPE_SHADED_COUNTY_BORDER_LAYER_ID}
              type="fill"
              paint={{
                "fill-color": [
                  "interpolate",
                  ["linear"],
                  ["^", ["coalesce", ["get", "totalCaseCount"], 0], COUNTY_SHADING_BASE],
                  0, minCaseCountColor,
                  sqrtMaxCaseCount, maxCaseCountColor
                ],
                "fill-opacity": providerLandscapeMapDataContext.providerLandscapeShadedCountyData ? 1 : 0
              }}
            />
          </Source>

          {providerLandscapeMapDataContext.providerLandscapeShadedCountyData && filtersContext.selectedState && (
            <>
              <ProviderLandscapeCountySummary isCountySelectionPopup={false} />
              <ProviderLandscapeCountySummary isCountySelectionPopup={true} />
            </>
          )}

          {providerLandscapeMapDataContext.providerLandscapeShadedCountyData && filtersContext.selectedState && (
            <GradientLegend
              qesTestId={`market-expansion-dashboard__provider-landscape-legend`}
              maxColor={maxCaseCountColor}
              maxValue={maxCaseCount}
              minColor={minCaseCountColor}
              legendTitle="County Cases" /> 
          )}

          <MapAppearanceControl
            colorThemeContext={ProviderLandscapeColorTheme.Context}
            colorThemeFlyoutTitle="Provider Landscape Map Color Theme"
            colorThemeSelectLabel="Color Theme"
            mapRef={mapRef}
            position="bottom-right"
            testId="market-expansion-provider-landscape"
          />

          <RecenterControl
            ignoreInMapExport={true}
            imagePath={`${imagesDir}Recenter.png`}
            mapRef={mapRef}
            onClick={handleRecenterIconClick}
            position="bottom-right"
          />

          <NavigationControl data-html2canvas-ignore="true" position="bottom-right" showCompass={false} />

        </ReactMapGL.Map>
      </div>
    </MapboxSupportAlert>
  );
}

export const ProviderLandscapeMap: FC = (): JSX.Element => {
  const { errorLoadingSettings, loadingDefaultSettings } = useContext(MappingContext);
  if (loadingDefaultSettings) {
    return (<div className="market-expansion-dashboard__map-container" data-testid="market-expansion__landscape-map__loading">
      <LoadingSpinner />
    </div>);
  } else if (errorLoadingSettings) {
    return (<div className="market-expansion-dashboard__map-container" role="alert">An error has occurred</div>);
  };

  return (
    <ProviderLandscapeColorTheme.Provider>
      <WrappedProviderLandscapeMap />
    </ProviderLandscapeColorTheme.Provider>
  );
};